<template>
  <div>
    <tool-bar :title="title" subTitle=""></tool-bar>
    <loading :loading="loading"></loading>

    <error-alert-item
      :error-alert-event="errorAlertEvent"
      :network-error="networkError"
      @resetErrorAlertEvent="errorAlertEvent=false"
    ></error-alert-item>

    <activity-redirect-item
      :loan-tasks="tasks"
      :current-loan-task="currentTaskName"
      :is-loan-task="false"
      :redirect-activity="redirectActivity"
    ></activity-redirect-item>

    <stage-comment-item></stage-comment-item>

    <refresh-item
      :show="showRefreshItem"
      @onRefresh="fetchInitialData"
    ></refresh-item>

    <file-download
      :download="downloadDeliveryAdviceFile"
      :link="sampleDeliveryAdviceLink"
      :save-file="true"
      @fileDownloaded="downloadDeliveryAdviceFile=false"
      @fileNotDownloaded="downloadDeliveryAdviceFile=false"
      :document-name="deliveryAdviceDocumentName"
      file-extension="pdf"
    ></file-download>

    <file-upload-item
      ref="fileUploadItem"
      :show-message-on-upload="false"
      @onDocumentUploaded="onDocumentUploaded"
    ></file-upload-item>

    <div v-if="showUI" class="margin-top-40">
      <button-with-image
        class="horizontal-center"
        text="Download Delivery Advice"
        :attach-image-des-src="fileDownloadImageSrc"
        :enabled-btn-style="downloadDeliveryButtonStyle"
        :enabled-text-style="{color: 'black'}"
        @onButtonClicked="downloadDeliveryAdvice"
        :image-src="fileDownloadImageSrc"
      ></button-with-image>

      <file-attach-button
        class="margin-top-30"
        file-attach-button-id="attach_invoice"
        text="Attach Invoice Copy"
        :attach-image-des-src="fileAttachDescription"
        @filesAttached="invoiceCopyAttached($event.files[0])"
      ></file-attach-button>

      <wxc-button
        text="Next"
        class="horizontal-center margin-top-40 margin-bottom-20"
        type="blue"
        @wxcButtonClicked="onSubmit"
      ></wxc-button>
    </div>
  </div>
</template>

<script>
import WxcButton from "../../../weex-ui/packages/wxc-button/index";
import DataSource from "../../data/DataSource";
import LoanScopeDataHolder from "../../data/model/LoanScopeDataHolder";
import ToolBar from "../layout/ToolBar";
import Loading from "../layout/Loading";
import FileDownload from "../layout/FileDownload";
import ErrorAlertItem from "../layout/ErrorAlertItem";
import FileAttachButton from "../layout/FileAttachButton";
import StageCommentItem from "../layout/StageCommentItem";
import ActivityRedirectItem from "../layout/ActivityRedirectItem";
import { DataTables } from "../../utils/DataTables";
import { EntityType } from "../../utils/EntityType";
import { Activities } from "../../utils/ActivityNavigator";
import DateUtils, { DateFormats } from "../../utils/DateUtils";
import { MessageConstants } from "../../utils/MessageConstantUtil";
import LoanTasksActivityNavigator from "../../utils/LoanTasksActivityNavigator";
import AlertService from "../core/alert/AlertService";
import { environment } from "../../environments/environment";
import { DocumentDescription, Documents } from "../../utils/DocumentDetails";
import RefreshItem from "../layout/RefreshItem";
import FileUploadItem from "../layout/FileUploadItem";
import { ImageResourcesUtil } from "../../utils/ImageResourcesUtil";
import ButtonWithImage from "../layout/ButtonWithImage";

export default {
  name: "InvoiceActivity",
  components: {
    WxcButton,
    ErrorAlertItem,
    Loading,
    FileAttachButton,
    FileDownload,
    ActivityRedirectItem,
    ToolBar,
    StageCommentItem,
    RefreshItem,
    FileUploadItem,
    ButtonWithImage
  },

  data: () => ({
    title: "Invoice",
    loading: false,
    showRefreshItem: false,
    errorAlertEvent: false,
    networkError: {},
    sampleDeliveryAdviceLink: "",
    downloadDeliveryAdviceFile: false,
    invoiceDetailsUploaded: false,
    showUI: false
  }),

  methods: {
    showError(error) {
      this.loading = false;
      this.networkError = error;
      this.errorAlertEvent = true;
    },

    showToast(message) {
      AlertService.showToast(message);
    },

    downloadDeliveryAdvice() {
      if (this.sampleDeliveryAdviceLink) {
        this.downloadDeliveryAdviceFile = true;
      } else {
        this.showToast(MessageConstants.DELIVERY_ADVICE_NOT_FOUND);
      }
    },

    invoiceCopyAttached(file) {
      this.$refs.fileUploadItem.createDocument(
        EntityType.LOANS,
        LoanScopeDataHolder.getLoanId(),
        DocumentDescription.PRODUCT_INVOICE.name,
        DocumentDescription.PRODUCT_INVOICE.description,
        DocumentDescription.PRODUCT_INVOICE.directory,
        file
      );
    },

    onDocumentUploaded(documentUploadData) {
      if (documentUploadData && documentUploadData.documentUploadResponse) {
        this.loading = true;
        const resourceId = documentUploadData.documentUploadResponse.resourceId;

        //update invoice doc id in loan details
        const loanDetailsData = {};
        loanDetailsData["invoice_doc_id"] = resourceId;
        loanDetailsData["locale"] = "en";
        DataSource.updateDataTableEntry(
          DataTables.LOAN_DETAILS,
          LoanScopeDataHolder.getLoanId(),
          loanDetailsData
        )
          .then(loanDetailsUpdateResponse => {
            if (loanDetailsUpdateResponse.errorOccurred) {
              this.showError(loanDetailsUpdateResponse.data);
            } else {
              //create or update invoice details table
              const invoiceDetailsData = {};
              invoiceDetailsData["doc_id"] = resourceId;
              invoiceDetailsData["date"] =
                DateUtils.getCurrentTimeAsFormat(DateFormats.DD_MMMM_YYYY);
              invoiceDetailsData["locale"] = "en";

              DataSource.getDataTableInfo(
                DataTables.INVOICE_DETAILS,
                LoanScopeDataHolder.getLoanId()
              )
                .then(invoiceDetailsTableRes => {
                  if (invoiceDetailsTableRes.errorOccurred) {
                    this.showError(invoiceDetailsTableRes.data);
                  } else {
                    if (invoiceDetailsTableRes.data.length > 0) {
                      //update
                      DataSource.updateDataTableEntry(
                        DataTables.INVOICE_DETAILS,
                        LoanScopeDataHolder.getLoanId(),
                        invoiceDetailsData
                      )
                        .then(updateInvoiceDetailsRes => {
                          this.loading = false;
                          if (updateInvoiceDetailsRes.errorOccurred) {
                            this.showError(updateInvoiceDetailsRes.data)
                          } else {
                            this.invoiceDetailsUploaded = true;
                            this.showToast(MessageConstants.INVOICE_DETAILS_UPLOADED);
                          }
                        })
                    } else {
                      //create
                      DataSource.addDataTableEntry(
                        DataTables.INVOICE_DETAILS,
                        LoanScopeDataHolder.getLoanId(),
                        invoiceDetailsData
                      )
                        .then(addInvoiceDetailsRes => {
                          this.loading = false;
                          if (addInvoiceDetailsRes.errorOccurred) {
                            this.showError(addInvoiceDetailsRes.data);
                          } else {
                            this.invoiceDetailsUploaded = true;
                            this.showToast(MessageConstants.INVOICE_DETAILS_UPLOADED);
                          }
                        })
                    }
                  }
                })
            }
          })
      }
    },

    onSubmit() {
      if (this.checkForValidation()) {
        this.completeCurrentTaskAndGetNextTask();
      }
    },

    checkForValidation() {
      if (!this.invoiceDetailsUploaded) {
        this.showToast(MessageConstants.UPLOAD_INVOICE_DETAILS);
        return false;
      }

      return true;
    },

    completeCurrentTaskAndGetNextTask() {
      this.loading = true;
      LoanTasksActivityNavigator.getNextTask(this.tasks)
        .then(res => {
          this.loading = false;
          if (res.errorOccurred) {
            this.showError(res.data);
          }
        });
    },

    fetchInitialData() {
      this.showRefreshItem = false;
      this.loading = true;
      DataSource.getDocuments(EntityType.LOANS, LoanScopeDataHolder.getLoanId())
        .then(documentsResponse => {
          this.loading = false;
          if (documentsResponse.errorOccurred) {
            this.showError(documentsResponse.data);
            this.showRefreshItem = true;
          } else {
            this.showUI = true;
            const documents = documentsResponse.data;
            documents && documents.forEach(document => {
              if (document && Documents.DELIVERY_ADVICE_DOCUMENT === document.name) {
                this.sampleDeliveryAdviceLink = `${environment.serverUrl}/loans/${LoanScopeDataHolder.getLoanId()}/documents/${document.id}/attachment?tenantIdentifier=default`;
              }
            });
          }
        })
    }
  },

  created() {
    this.fetchInitialData();
  },

  computed: {
    redirectActivity() {
      return Activities.LOAN_DETAILS;
    },

    tasks() {
      return [Activities.INVOICE];
    },

    currentTaskName() {
      return LoanScopeDataHolder.getCurrentLoanTask()
        ? LoanScopeDataHolder.getCurrentLoanTask().name
        : "";
    },

    deliveryAdviceDocumentName() {
      return `Delivery_advice_${LoanScopeDataHolder.getLoanAccountNumber()}`;
    },

    fileAttachDescription() {
      return this.invoiceDetailsUploaded
        ? ImageResourcesUtil.getImageLink(ImageResourcesUtil.TICK_GREEN)
        : ImageResourcesUtil.getImageLink(ImageResourcesUtil.CAMERA_ADD);
    },

    fileDownloadImageSrc() {
      return ImageResourcesUtil.getImageLink(ImageResourcesUtil.FILE_DOWNLOAD);
    },

    downloadDeliveryButtonStyle() {
      return {
        "background-color": "#AEDEF4",
        "border-color": "#96BFD2",
        "border-width": "1px",
        "border-style": "solid"
      };
    }
  }
};
</script>

<style
  scoped
  src="../../assets/style/base.css"></style>


